import { useEffect, useState } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const TYPE_KEYS = [
  { key: 'news',       icon: '📰', color: 'var(--cyan)',    tKey: 'type_news' },
  { key: 'discount',   icon: '🎁', color: 'var(--orange)', tKey: 'type_discount' },
  { key: 'event',      icon: '🎉', color: 'var(--purple)', tKey: 'type_event' },
  { key: 'tournament', icon: '🏆', color: 'var(--yellow)', tKey: 'type_tournament' },
];

const getTypes = (tr) => TYPE_KEYS.map(x => ({ ...x, label: tr(x.tKey) }));
const typeMeta = (typeKey, types) => types.find(x => x.key === typeKey) || types[0];

function fmtDT(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getDate()}/${d.getMonth() + 1} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Announcements() {
  const { t } = useLang();
  const TYPES = getTypes(t);
  const { labId, currentLab } = useLab();
  const [items, setItems] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    type: 'news', title: '', body: '', image_url: '', cta_url: '', ends_at: '',
    is_pinned: false, send_push: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isSubActive =
    sub && (
      (sub.status === 'trial'  && sub.trial_ends_at      && new Date(sub.trial_ends_at)      > new Date()) ||
      (sub.status === 'active' && sub.current_period_end && new Date(sub.current_period_end) > new Date())
    );

  const load = async () => {
    if (!labId) return;
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([
        api.get(`/announcements?lab_id=${labId}`).then(r => r.data),
        api.get('/subscriptions/my').then(r => r.data).catch(() => null),
      ]);
      setItems(aRes);
      setSub(sRes);
    } catch (e) {
      setError(e.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [labId]);

  const create = async () => {
    setError(''); setSuccess('');
    if (!form.title.trim() || !form.body.trim()) {
      setError(t('errTitleBody'));
      return;
    }
    setCreating(true);
    try {
      const payload = {
        lab_id: labId,
        type: form.type,
        title: form.title.trim(),
        body: form.body.trim(),
        image_url: form.image_url.trim() || null,
        cta_url: form.cta_url.trim() || null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        is_pinned: form.is_pinned,
        send_push: form.send_push,
      };
      const r = await api.post('/announcements', payload);
      setSuccess(`${t('newAnnouncement')} ✓. Push: ${r.data.push_sent_count || 0}`);
      setForm({ type: 'news', title: '', body: '', image_url: '', cta_url: '', ends_at: '', is_pinned: false, send_push: true });
      await load();
    } catch (e) {
      if (e.response?.status === 402) {
        setError(t('errSubRequired'));
      } else {
        setError(e.response?.data?.error || t('error'));
      }
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('confirmDeleteAnn'))) return;
    try {
      await api.delete(`/announcements/${id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || t('error'));
    }
  };

  const togglePin = async (item) => {
    try {
      await api.put(`/announcements/${item.id}`, { is_pinned: !item.is_pinned });
      await load();
    } catch (e) {
      if (e.response?.status === 402) setError(t('errSubOnly'));
    }
  };

  const archive = async (item) => {
    try {
      await api.put(`/announcements/${item.id}`, { status: item.status === 'archived' ? 'active' : 'archived' });
      await load();
    } catch (e) {
      if (e.response?.status === 402) setError(t('errSubOnly'));
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>{t('announcementsTitle')}</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13 }}>
          {t('annSubtitle')}
          {currentLab && <> {t('annBranchLabel')} <b style={{ color: 'var(--cyan)' }}>{currentLab.name}</b></>}
        </div>
      </div>

      {/* OBUNA BADGE */}
      {!isSubActive && (
        <div style={{
          background: 'rgba(255,59,92,0.08)', border: '1px solid var(--red)',
          borderRadius: 12, padding: 16, marginBottom: 20,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{ color: 'var(--red)', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
              🔒 {t('subInactive')}
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 12 }}>
              {t('subInactiveDesc')}
            </div>
          </div>
          <a href="/obuna" style={{
            background: 'var(--cyan)', color: '#000', padding: '8px 16px',
            borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 13,
          }}>{t('viewSubscription')}</a>
        </div>
      )}

      {isSubActive && sub && (
        <div style={{
          background: 'rgba(0,255,136,0.06)', border: '1px solid var(--green)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 12,
        }}>
          ✅ {t('subActive')} ({sub.status === 'trial' ? t('trialPeriod') : sub.plan_name || t('subActive')}).
          {sub.days_left != null && <> {t('daysLeftAnn')}: <b>{sub.days_left} {t('dayUnit')}</b></>}
        </div>
      )}

      {/* FORMA — yangi e'lon */}
      <div style={{
        background: 'var(--card2)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 20, marginBottom: 24, opacity: isSubActive ? 1 : 0.55,
      }}>
        <div style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>
          ✍️ {t('newAnnouncement')}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {TYPES.map(tp => (
            <button key={tp.key} onClick={() => setForm({ ...form, type: tp.key })} disabled={!isSubActive}
              style={{
                background: form.type === tp.key ? `${tp.color}20` : 'transparent',
                border: `1px solid ${form.type === tp.key ? tp.color : 'var(--border)'}`,
                color: form.type === tp.key ? tp.color : 'var(--text2)',
                padding: '8px 14px', borderRadius: 8, cursor: isSubActive ? 'pointer' : 'not-allowed',
                fontSize: 12, fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center',
              }}>
              <span>{tp.icon}</span> {tp.label}
            </button>
          ))}
        </div>

        <input type="text" placeholder={t('annTitlePlh')}
          value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
          disabled={!isSubActive} maxLength={200}
          style={inputStyle} />

        <textarea placeholder={t('annBodyPlh')}
          value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
          disabled={!isSubActive} maxLength={2000} rows={4}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <input type="url" placeholder={t('imageUrlOptional')}
            value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })}
            disabled={!isSubActive} style={inputStyle} />
          <input type="url" placeholder={t('ctaUrlOptional')}
            value={form.cta_url} onChange={e => setForm({ ...form, cta_url: e.target.value })}
            disabled={!isSubActive} style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
          <label style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {t('expiryOptional')}
            <input type="datetime-local" value={form.ends_at} onChange={e => setForm({ ...form, ends_at: e.target.value })}
              disabled={!isSubActive} style={{ ...inputStyle, margin: 0, width: 220 }} />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_pinned} onChange={e => setForm({ ...form, is_pinned: e.target.checked })}
              disabled={!isSubActive} />
            📌 {t('pinToTop')}
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.send_push} onChange={e => setForm({ ...form, send_push: e.target.checked })}
              disabled={!isSubActive} />
            🔔 {t('sendPushLabel')}
          </label>
        </div>

        {error && <div style={{ color: 'var(--red)', marginTop: 12, fontSize: 13 }}>{error}</div>}
        {success && <div style={{ color: 'var(--green)', marginTop: 12, fontSize: 13 }}>{success}</div>}

        <button onClick={create} disabled={!isSubActive || creating}
          style={{
            marginTop: 16, background: isSubActive ? 'var(--cyan)' : 'var(--border)',
            color: isSubActive ? '#000' : 'var(--text3)', border: 'none',
            padding: '12px 24px', borderRadius: 8, fontWeight: 700, fontSize: 14,
            cursor: (isSubActive && !creating) ? 'pointer' : 'not-allowed',
          }}>
          {creating ? t('sending') : t('sendAnnouncement')}
        </button>
      </div>

      {/* RO'YXAT */}
      <div style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
        📋 {t('annListHeader')} ({items.length})
      </div>

      {loading ? (
        <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>{t('loading')}</div>
      ) : items.length === 0 ? (
        <div style={{
          background: 'var(--card2)', border: '1px dashed var(--border)',
          borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--text3)',
        }}>
          {t('noAnnouncements')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(item => {
            const m = typeMeta(item.type, TYPES);
            const archived = item.status === 'archived';
            return (
              <div key={item.id} style={{
                background: 'var(--card2)',
                border: `1px solid ${item.is_pinned ? m.color : 'var(--border)'}`,
                borderLeft: `4px solid ${m.color}`,
                borderRadius: 12, padding: 16, opacity: archived ? 0.5 : 1,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ background: `${m.color}20`, color: m.color, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                        {m.icon} {m.label}
                      </span>
                      {item.is_pinned && <span style={{ fontSize: 11, color: 'var(--yellow)' }}>📌 {t('annPinned')}</span>}
                      {archived && <span style={{ fontSize: 11, color: 'var(--text3)' }}>🗄 {t('annArchived')}</span>}
                      {item.push_sent && (
                        <span style={{ fontSize: 11, color: 'var(--green)' }}>
                          ✅ {t('annPushSent')} ({item.push_sent_count || 0})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap', marginBottom: 6 }}>{item.body}</div>
                    {item.image_url && (
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>🖼 {item.image_url}</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                      {t('annCreatedAt')}: {fmtDT(item.created_at)}
                      {item.ends_at && <> · {t('expiresOn')}: {fmtDT(item.ends_at)}</>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button onClick={() => togglePin(item)} disabled={!isSubActive} style={btnGhost}>
                      {item.is_pinned ? t('unpin') : `📌 ${t('pinBtn')}`}
                    </button>
                    <button onClick={() => archive(item)} disabled={!isSubActive} style={btnGhost}>
                      {archived ? t('activateBtn') : `🗄 ${t('archiveBtn')}`}
                    </button>
                    <button onClick={() => remove(item.id)} style={{ ...btnGhost, borderColor: 'var(--red)', color: 'var(--red)' }}>
                      🗑 {t('deleteBtn')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)',
  color: 'var(--text)', padding: '10px 12px', borderRadius: 8, fontSize: 13,
  marginBottom: 12, boxSizing: 'border-box',
};

const btnGhost = {
  background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
  padding: '6px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
};
